import { Box, Card, Grid, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowUpRight, Clock } from 'lucide-react'
import { articles, articlesIntro } from '@/content/articles'
import { productImagesFor } from '@/content/products'
import { colors, fonts, layout } from '@/theme'
import { Section } from '@/components/ui/Section'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { Breadcrumbs } from '@/components/ui/Breadcrumbs'
import { Media } from '@/components/ui/Media'
import { Reveal } from '@/components/motion/Reveal'
import { Seo } from '@/components/seo/Seo'

export function Articles() {
  return (
    <>
      <Seo title="Articles & Guides" path="/articles" description="Practical guidance on welding protection, glove selection, materials and care from True Forge." />

      <Section sx={{ pb: 0 }}>
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Articles' }]} />
        <Eyebrow>Knowledge</Eyebrow>
        <Typography variant="h1" component="h1" sx={{ maxWidth: 940 }}>
          Articles &amp; guides
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 3, maxWidth: layout.contentMaxWidth }}>
          {articlesIntro}
        </Typography>
      </Section>

      <Section>
        <Grid container spacing={{ xs: 4, md: 4 }}>
          {articles.map((a, i) => {
            const gallery = productImagesFor(a.image.sku)
            const img = gallery[a.image.index] ?? gallery[0]
            return (
              <Grid key={a.slug} size={{ xs: 12, sm: 6, md: 4 }}>
                <Reveal delay={i * 0.04}>
                  <Card
                    component={RouterLink}
                    to={`/articles/${a.slug}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      textDecoration: 'none',
                      transition: 'border-color 300ms ease, transform 400ms cubic-bezier(.2,.8,.2,1), box-shadow 400ms ease',
                      '&:hover': { borderColor: 'primary.main', transform: 'translateY(-6px)', boxShadow: '0 24px 48px -24px rgba(0,0,0,0.8)' },
                      '&:hover img': { transform: 'scale(1.05)' },
                      '&:hover .art-arrow': { color: colors.gold, transform: 'translate(2px, -2px)' },
                    }}
                  >
                    <Box sx={{ bgcolor: colors.surfaceAlt, overflow: 'hidden' }}>
                      {img && <Media src={img.src} srcSm={img.srcSm} alt={a.title} width={img.width} height={img.height} ratio={16 / 10} imgSx={{ transition: 'transform 600ms ease' }} />}
                    </Box>
                    <Stack sx={{ flex: 1, p: 3 }} spacing={1.25}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="overline" sx={{ color: 'primary.main' }}>{a.category}</Typography>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.grey }} />
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                          <Clock size={13} />
                          <Typography variant="caption">{a.readMins} min read</Typography>
                        </Stack>
                      </Stack>
                      <Typography variant="h5" component="h2" sx={{ fontSize: { xs: 22, md: 24 } }}>{a.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>{a.excerpt}</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, color: 'text.secondary', fontFamily: fonts.label }}>
                        <Typography variant="subtitle2" component="span">Read article</Typography>
                        <ArrowUpRight size={16} className="art-arrow" style={{ transition: 'color 300ms ease, transform 300ms ease' }} />
                      </Stack>
                    </Stack>
                  </Card>
                </Reveal>
              </Grid>
            )
          })}
        </Grid>
      </Section>
    </>
  )
}
